import { memo } from 'react';
import { sameNodeContent } from '../../Services/GraphRenderState';
import { Node, NodeProps } from '@xyflow/react';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';

import { ParameterType } from '../../../../api/api';
import useFlowActivityMetadata from '../../Hooks/Metadata/useFlowActivityMetadata';
import { FlowRefModel } from '../../Models/DomainServiceModel';
import { ActivityModel } from '../../Models/FlowModel';
import ActivityIcon from '../ActivityIcon';
import DisplayContentTemplate from '../DisplayContentTemplate';
import { ActivityCategories, ActivityTypes } from '../../Models/ActivityConstants';
import './ActivityNode.scss';
import BaseNode from './BaseNode';
import CompactAgentServiceIcon from './CompactAgentServiceIcon';
import AgentActivityNodeContent from './AgentActivityNodeContent';

export const ACTIVITY_NODE_TYPE = 'activity';

function ActivityNode({ data, selected, id }: NodeProps & { data: ActivityModel }) {
	const activityMetadata = useFlowActivityMetadata(data.Type);

	const flowRefParam = activityMetadata?.parameters?.find((p) => p.type === ParameterType.FlowRef);
	const flowRefValue = flowRefParam ? (data[flowRefParam.name] as FlowRefModel | undefined) : undefined;
	const flowId = flowRefValue?.Id;

	const isCompact = data.Metadata?.compact === true && activityMetadata?.categories?.includes(ActivityCategories.AGENT_SERVICE) === true;
	const configuredAgentId = typeof data.AgentId === 'string' ? data.AgentId.trim() : '';
	const isAgent = data.Type === ActivityTypes.AGENT;
	const agentId = isAgent && configuredAgentId && configuredAgentId !== '00000000-0000-0000-0000-000000000000'
		? configuredAgentId : undefined;
	const nodeClassName = `activity-node${flowId ? ' activity-node--has-flow' : ''}${isCompact ? ' activity-node--compact' : ''}${isAgent && !isCompact ? ' activity-node--agent-avatar' : ''}`;

	return (
		<>
			<BaseNode id={id} data={data} selected={selected} activityMetadata={activityMetadata} nodeClassName={nodeClassName}>
				{flowId && (
					<IconButton
						component="a"
						href={`/flow/${flowId}`}
						target="_blank"
						rel="noopener noreferrer"
						title="Open Flow"
						size="small"
						onClick={(e: React.MouseEvent) => e.stopPropagation()}
						sx={{ position: 'absolute', top: 0, right: 0, zIndex: 10, opacity: 0.6, padding: '2px' }}
					>
						<OpenInNewIcon sx={{ fontSize: '0.75rem' }} />
					</IconButton>
				)}
				{isCompact ? <CompactAgentServiceIcon activity={data} activityMetadata={activityMetadata} /> : isAgent ? <AgentActivityNodeContent agentId={agentId} activity={data} metadata={activityMetadata} /> : (
					<div className="activity-node-content">
						<Box className="activity-node-icon">
							<ActivityIcon activityMetadata={activityMetadata} showTriggerBadge triggerBadgeSize="small" maxWidth="24px" />
						</Box>
						<DisplayContentTemplate
							template={activityMetadata?.displayContent}
							fallbackText={activityMetadata?.displayName ?? data.Type}
							context={data}
							className="label"
						/>
					</div>
				)}
			</BaseNode>
		</>
	);
}

export function nodeFromActivity(model: ActivityModel, x: number = 0, y: number = 0): Node<ActivityModel> {
	return {
		id: model.Id,
		type: ACTIVITY_NODE_TYPE,
		position: { x, y },
		data: model,
	} as Node<ActivityModel>;
}

export default memo(ActivityNode, sameNodeContent);
